import type { Location } from '@mapsindoors/typescript-interfaces';
import type { FetcherContext, RoomWithGeometry } from './types.js';

export const loadRoomsWithGeometry = async (
  rooms: Location[],
  context: FetcherContext,
): Promise<RoomWithGeometry[]> => {
  const locationService = window.mapsindoors?.services?.LocationsService;
  if (!locationService) {
    throw new Error('MapsIndoors location service is not available');
  }

  const results = await Promise.all(
    rooms.map(async (room) => {
      const cachedGeometry = context.state.geometryCache.get(room.id);
      if (cachedGeometry) {
        return { room, geometry: cachedGeometry } as RoomWithGeometry;
      }

      if (context.state.missingGeometry.has(room.id)) {
        return null;
      }

      try {
        const location = await locationService.getLocation(room.id);
        if (!location?.geometry) {
          console.warn(`No geometry found for room ${room.id}. Skipping in overlay.`);
          context.state.missingGeometry.add(room.id);
          return null;
        }

        const geometry = location.geometry as RoomWithGeometry['geometry'];
        context.state.geometryCache.set(room.id, geometry);
        return { room, geometry } as RoomWithGeometry;
      } catch (error) {
        console.warn(
          `Unable to load geometry for room ${room.id}. Skipping from overlay updates until restart.`,
          error,
        );
        context.state.missingGeometry.add(room.id);
        return null;
      }
    }),
  );

  return results.filter((entry): entry is RoomWithGeometry => entry !== null);
};
